import { useScrollReveal } from '@/hooks/useScrollReveal';

const producerImpacts = [
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Fair Pricing, No Panic Sales',
    desc: 'When you can freeze and store your harvest instead of selling it immediately at whatever price the middleman offers, quality sets the price — not urgency.',
  },
  {
    icon: 'ri-archive-line',
    title: 'Your Harvest, Your Timeline',
    desc: 'Access freezing and storage capacity near your farm-gate or landing point. Sell when the market is right — days, weeks, or months after harvest.',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Demand Visibility',
    desc: 'Our platform shows you what buyers are searching for, in what volumes, and at what price points — so you can plan production, not guess.',
  },
];

const consumerImpacts = [
  {
    icon: 'ri-shield-star-line',
    title: 'Food You Can Trust',
    desc: 'Every product carries a standard specification. What you see on the spec sheet is exactly what arrives — cut, graded, and frozen to standard.',
  },
  {
    icon: 'ri-thermometer-line',
    title: 'Cold-Chain Verified, Every Time',
    desc: 'Temperature is logged from freezer to doorstep. If the cold chain breaks, the order is replaced — no forms, no arguments.',
  },
  {
    icon: 'ri-price-tag-3-line',
    title: 'Seasonal Quality, Year-Round Price',
    desc: 'Because produce is frozen at peak — when supply is abundant and quality is highest — you get better quality at more stable prices than the fresh market.',
  },
];

export default function ImpactSection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-16 md:py-20 bg-secondary-50/30">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100/70 border border-primary-200/50 mb-4">
            <span className="text-xs font-semibold text-primary-700 uppercase tracking-wider">Our Impact</span>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary-700 mb-4">
            Better for Producers. Safer for Consumers.
          </h2>
          <p className="text-foreground-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            The cold chain isn&apos;t just infrastructure — it&apos;s economics. When producers get fair prices and consumers get safe food, the whole system wins.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Producers column */}
          <div>
            <div className="flex items-center gap-3 mb-7">
              <div className="w-11 h-11 rounded-xl bg-brand-600 flex items-center justify-center">
                <i className="ri-user-star-line text-xl text-paper-50"></i>
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-primary-700">For Producers</h3>
                <p className="text-xs text-foreground-500">Farmers, Fishermen, Processors</p>
              </div>
            </div>
            <div className="space-y-4">
              {producerImpacts.map((item) => (
                <div key={item.title} className="group bg-card-50 rounded-xl border border-primary-200/30 p-5 shadow-sm hover:shadow-md hover:border-primary-300/50 transition-all cursor-default card-animate">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors animate-icon-float icon-hover-pop">
                      <i className={`${item.icon} text-lg text-primary-600`}></i>
                    </div>
                    <div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">{item.title}</h4>
                      <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Consumers column */}
          <div>
            <div className="flex items-center gap-3 mb-7">
              <div className="w-11 h-11 rounded-xl bg-accent-500 flex items-center justify-center">
                <i className="ri-heart-line text-xl text-brand-800"></i>
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-accent-700">For Consumers</h3>
                <p className="text-xs text-foreground-500">Families, Restaurants, Hotels</p>
              </div>
            </div>
            <div className="space-y-4">
              {consumerImpacts.map((item) => (
                <div key={item.title} className="group bg-card-50 rounded-xl border border-accent-200/30 p-5 shadow-sm hover:shadow-md hover:border-accent-300/50 transition-all cursor-default card-animate">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-200 transition-colors animate-icon-float icon-hover-pop">
                      <i className={`${item.icon} text-lg text-accent-600`}></i>
                    </div>
                    <div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">{item.title}</h4>
                      <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom stat callout */}
        <div className="mt-12 bg-gradient-to-r from-primary-600/5 via-accent-500/10 to-primary-600/5 rounded-2xl border border-primary-200/20 p-8 text-center shadow-sm">
          <p className="text-foreground-700 text-sm leading-relaxed max-w-2xl mx-auto">
            <strong className="text-primary-700 font-semibold">The result:</strong> Producers who join Freshhlock report an average{' '}
            <strong className="text-accent-600 font-semibold">18-24% improvement in realised prices</strong> compared to selling at harvest-time to mandi middlemen. Consumers get standardised quality at prices that don&apos;t swing with every season. That&apos;s the two-sided network effect in action.
          </p>
        </div>
      </div>
    </section>
  );
}